import { useContext } from "react";
import { ThemeContext } from "../theme/ThemeContext";
import { tokens, darkTokens } from "../tokens";

/**
 * useTokens 鉤子
 * 根據當前主題模式返回相應的設計 Token
 * 在深色模式下返回 darkTokens，在淺色模式下返回 tokens
 * 如果 tokens 未定義，則返回後備值以確保應用不會崩潰
 */

// 後備 tokens 定義，確保即使原始 tokens 未載入也能提供基本樣式
const fallbackTokens = {
  colors: {
    primary: {
      50: "#FFF7ED",
      100: "#FFEDD5",
      200: "#FED7AA",
      300: "#FDBA74",
      400: "#FB923C",
      500: "#F97316",
      600: "#EA580C",
      700: "#C2410C",
      800: "#9A3412",
      900: "#7C2D12",
    },
    secondary: {
      50: "#EFF6FF",
      100: "#DBEAFE",
      200: "#BFDBFE",
      300: "#93C5FD",
      400: "#60A5FA",
      500: "#3B82F6",
      600: "#2563EB",
      700: "#1D4ED8",
      800: "#1E40AF",
      900: "#1E3A8A",
    },
    gray: {
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
      dark: {
        default: "#111827",
        paper: "#1F2937",
      },
    },
    border: { light: "#E5E7EB", dark: "#374151" },
    text: {
      primary: "#1F2937",
      secondary: "#4B5563",
    },
    error: {
      light: "#FEF2F2",
      main: "#EF4444",
      dark: "#B91C1C",
    },
  },
  typography: {
    fontFamily: {
      base: "'Inter', system-ui, Avenir, Helvetica, Arial, sans-serif",
      mono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    },
    size: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.75rem",
      "3xl": "2.25rem",
      "4xl": "2.5rem",
    },
    weight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    letterSpacing: {
      tight: "-0.025em",
      normal: "0",
      wide: "0.025em",
    },
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem",
  },
  radius: {
    sm: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1.25rem",
    full: "9999px",
  },
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    colored:
      "0 10px 15px -3px rgba(249, 115, 22, 0.2), 0 4px 6px -2px rgba(249, 115, 22, 0.1)",
  },
  transition: {
    normal: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

export function useTokens() {
  const { isDarkMode } = useContext(ThemeContext);

  // 使用原始 tokens 或後備值
  const lightTokens = tokens || fallbackTokens;

  // 確保 darkTokens 有正確的 transition 屬性
  let darkModeTokens = darkTokens;
  if (!darkModeTokens) {
    // 如果 darkTokens 不存在，使用 lightTokens 並修改顏色
    darkModeTokens = {
      ...lightTokens,
      colors: {
        ...lightTokens.colors,
        background: {
          paper: "#1F2937",
          default: "#111827",
          dark: {
            paper: "#1F2937",
            default: "#111827",
          },
        },
        text: {
          primary: "#F9FAFB",
          secondary: "#D1D5DB",
          disabled: "#6B7280",
          dark: {
            primary: "#F9FAFB",
            secondary: "#D1D5DB",
            disabled: "#6B7280",
          },
        },
      },
    };
  }

  // 確保 transition 屬性存在
  if (lightTokens.transitions && !lightTokens.transition) {
    lightTokens.transition = lightTokens.transitions;
  }

  if (darkModeTokens.transitions && !darkModeTokens.transition) {
    darkModeTokens.transition = darkModeTokens.transitions;
  }

  return isDarkMode ? darkModeTokens : lightTokens;
}
